import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { Button } from '@heidi/design-system/button';
import { Combobox } from '@heidi/design-system/combobox';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { getIntegrationCapabilities } from '@heidi/integrations/connected-apps/data';
import { getFhirCapabilityStatement } from '@heidi/integrations/ehr/data';
import { EHRProvider } from '@heidi/schema/ml-scribe';
import { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { IntegrationDebugJsonResult } from './integration-debug-json-result';

const messages = defineMessages({
  heading: {
    defaultMessage: 'Capability statements',
    id: 'components.dev.integration.capabilities.heading',
  },
  description: {
    defaultMessage:
      "Fetches Heidi's Scribe capabilities for a provider and the FHIR R4 CapabilityStatement for the user's connected EHR.",
    id: 'components.dev.integration.capabilities.description',
  },
  providerLabel: {
    defaultMessage: 'Scribe capability provider',
    id: 'components.dev.integration.capabilities.providerLabel',
  },
  providerPlaceholder: {
    defaultMessage: 'Select an EHR provider',
    id: 'components.dev.integration.capabilities.providerPlaceholder',
  },
  providerSearchPlaceholder: {
    defaultMessage: 'Search EHR providers',
    id: 'components.dev.integration.capabilities.providerSearchPlaceholder',
  },
  noProviderResults: {
    defaultMessage: 'No EHR providers found.',
    id: 'components.dev.integration.capabilities.noProviderResults',
  },
  currentProvider: {
    defaultMessage: 'Current provider: {provider}',
    id: 'components.dev.integration.capabilities.currentProvider',
  },
  noCurrentProvider: {
    defaultMessage: 'No current integration provider was detected. Select one manually.',
    id: 'components.dev.integration.capabilities.noCurrentProvider',
  },
  getScribeStatement: {
    defaultMessage: 'Get Scribe capability statement',
    id: 'components.dev.integration.capabilities.getScribeStatement',
  },
  getFhirStatement: {
    defaultMessage: 'Get FHIR capability statement',
    id: 'components.dev.integration.capabilities.getFhirStatement',
  },
  scribeResult: {
    defaultMessage: 'Scribe capability statement',
    id: 'components.dev.integration.capabilities.scribeResult',
  },
  fhirResult: {
    defaultMessage: 'FHIR capability statement',
    id: 'components.dev.integration.capabilities.fhirResult',
  },
  scribeError: {
    defaultMessage: 'Could not fetch the Scribe capability statement.',
    id: 'components.dev.integration.capabilities.scribeError',
  },
  fhirError: {
    defaultMessage: 'Could not fetch the FHIR capability statement.',
    id: 'components.dev.integration.capabilities.fhirError',
  },
});

interface RequestState {
  data?: unknown;
  error?: string;
  isLoading: boolean;
}

const INITIAL_REQUEST_STATE: RequestState = { isLoading: false };

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

export function IntegrationCapabilitiesDevSection() {
  const intl = useIntl();
  const { integrationEhrProvider } = useIntegrationEhrInfo();
  const [providerOverride, setProviderOverride] = useState<string>();
  const [scribeState, setScribeState] = useState<RequestState>(INITIAL_REQUEST_STATE);
  const [fhirState, setFhirState] = useState<RequestState>(INITIAL_REQUEST_STATE);
  const selectedProvider = providerOverride ?? integrationEhrProvider;
  const providerItems = useMemo(
    () =>
      Object.values(EHRProvider)
        .map((provider) => ({ value: provider, label: provider }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    []
  );

  const handleGetScribeStatement = async () => {
    if (!selectedProvider) return;

    setScribeState({ isLoading: true });
    try {
      const { data } = await getIntegrationCapabilities(selectedProvider);
      setScribeState({ data, isLoading: false });
    } catch (error) {
      setScribeState({
        error: getErrorMessage(error, intl.formatMessage(messages.scribeError)),
        isLoading: false,
      });
    }
  };

  const handleGetFhirStatement = async () => {
    setFhirState({ isLoading: true });
    try {
      const { data } = await getFhirCapabilityStatement();
      setFhirState({ data, isLoading: false });
    } catch (error) {
      setFhirState({
        error: getErrorMessage(error, intl.formatMessage(messages.fhirError)),
        isLoading: false,
      });
    }
  };

  return (
    <section
      aria-labelledby="dev-tools-integration-capabilities-heading"
      className="mt-5"
    >
      <TypographyP2SemiBold
        id="dev-tools-integration-capabilities-heading"
        className="mb-2"
      >
        {intl.formatMessage(messages.heading)}
      </TypographyP2SemiBold>
      <div className="space-y-3">
        <TypographyCaption className="text-text-tertiary">
          {intl.formatMessage(messages.description)}
        </TypographyCaption>

        <div className="space-y-1.5">
          <TypographyCaption className="text-text-secondary">
            {intl.formatMessage(messages.providerLabel)}
          </TypographyCaption>
          <Combobox
            className="w-full"
            buttonClassName="w-full"
            contentClassName="z-[10001]"
            items={providerItems}
            value={selectedProvider}
            onValueChange={(provider) => setProviderOverride(provider ?? undefined)}
            placeholder={intl.formatMessage(messages.providerPlaceholder)}
            searchPlaceholder={intl.formatMessage(messages.providerSearchPlaceholder)}
            emptyText={intl.formatMessage(messages.noProviderResults)}
            searchBy="label"
            clearable={false}
          />
          <TypographyCaption className="text-text-tertiary">
            {integrationEhrProvider
              ? intl.formatMessage(messages.currentProvider, {
                  provider: integrationEhrProvider,
                })
              : intl.formatMessage(messages.noCurrentProvider)}
          </TypographyCaption>
        </div>

        <div className="space-y-3">
          <div className="space-y-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              disabled={!selectedProvider || scribeState.isLoading}
              onClick={() => {
                void handleGetScribeStatement();
              }}
            >
              {intl.formatMessage(messages.getScribeStatement)}
            </Button>
            <IntegrationDebugJsonResult
              title={intl.formatMessage(messages.scribeResult)}
              data={scribeState.data}
              error={scribeState.error}
              isLoading={scribeState.isLoading}
            />
          </div>

          <div className="space-y-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              disabled={fhirState.isLoading}
              onClick={() => {
                void handleGetFhirStatement();
              }}
            >
              {intl.formatMessage(messages.getFhirStatement)}
            </Button>
            <IntegrationDebugJsonResult
              title={intl.formatMessage(messages.fhirResult)}
              data={fhirState.data}
              error={fhirState.error}
              isLoading={fhirState.isLoading}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
